import { Grid } from "@mui/material";
import { SectionHeading } from "~/components/DetailsPage/components";
import { EmptySection } from "~/components/vulnerabilities";
import {
  AggregateScoreOrder,
  AggregateScoreOrderField,
  AggregateScoreType,
  OrderDirection,
  useGetAggregateScoresQuery,
} from "~/operations";
import { FirewatchList } from "~/components/FirewatchList";
import { Space } from "~/lib/types";
import { useSort } from "~/pages/inventory/hooks/useSort";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { usePaginate } from "~/components/pagination/usePaginate";

type SoftwareAdvisoriesProps = {
  space: Space;
  scope: SpaceOrWorkspaceScope;
  findingMrn: string;
};

export const SoftwareAdvisories = ({
  space,
  scope,
  findingMrn,
}: SoftwareAdvisoriesProps) => {
  const prefix = "advisory";

  const paginate = usePaginate({
    pageSizes: [3, 10, 25, 50],
    prefix,
  });
  const baseUrl = paginate.resetCursorUrl(location.href);

  const sort = useSort<AggregateScoreOrder>({
    baseUrl,
    defaultSort: {
      field: AggregateScoreOrderField.RiskValue,
      direction: OrderDirection.Asc,
    },
    validFields: [
      AggregateScoreOrderField.RiskValue,
      AggregateScoreOrderField.Rank,
      AggregateScoreOrderField.BlastRadius,
      AggregateScoreOrderField.Title,
    ],
    prefix,
  });

  const { data, loading } = useGetAggregateScoresQuery({
    variables: {
      entityMrn: scope.mrn,
      ...paginate.pageVars,
      orderBy: sort.orderBy,
      filter: {
        scoreType: AggregateScoreType.Advisory,
        softwareFindingMrn: findingMrn,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const advisories =
    data?.aggregateScores?.__typename === "AggregateScoresConnection"
      ? data.aggregateScores
      : undefined;

  const edges = advisories?.edges;
  const isEmpty = !loading && (!edges || edges?.length === 0);

  const paginateProps = paginate.pageProps(advisories?.pageInfo);

  return (
    <Grid id="version-chart" size={{ xs: 12 }}>
      <SectionHeading heading="Advisories" />
      {!isEmpty ? (
        <FirewatchList
          space={space}
          scope={scope}
          pageType={AggregateScoreType.Advisory}
          data={edges}
          loading={loading}
          sort={sort}
          paginateProps={paginateProps}
        />
      ) : (
        <EmptySection
          id="software-advisories-empty"
          text="There are currently no advisories for this software"
        />
      )}
    </Grid>
  );
};
